import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { fontWeights } from '../themes/theme';
import Box from '../Box';
import Flex from '../Flex';
import Relative from '../Relative';
import Text from '../Text';
import { StyledChevronDownIcon, StyledPlaceHolder, StyledSelect } from './assets/styles';

const propTypes = {
  /**
    Use the invalid state to indicate there was an error validating this field.
    */
  invalid: PropTypes.bool,
  /**
    Use the valid prop to indicate if the select was successfully validated
    filled; this will take precedence over the invalid prop.
    */
  valid: PropTypes.bool,
  /**
   * Shows an error message, it will only display if invalid is `true`.
   */
  errorMessage: PropTypes.string,
  /**
   * Floating label text.
   */
  placeholder: PropTypes.string,
  /**
   * Execute function when the component gets focus, this will be executed
   * after moving the floating label.
   */
  onFocus: PropTypes.func,
  /**
   * Execute function when the `Select` component losses focus, this will be
   * executed after moving the floating label.
   */
  onBlur: PropTypes.func,
  /**
   * Value to select if the Select component is used as a controlled input.
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Value to be used when the Select component is not used as a controlled
   * input.
   */
  defaultValue: PropTypes.string,
  /**
   * Custom border color when not in a valid or invalid state.
   */
  borderColor: PropTypes.string,
  /**
   * Custom text color for the floating label.
   */
  color: PropTypes.string
};

const defaultProps = {
  invalid: false,
  valid: false,
  errorMessage: '',
  placeholder: '',
  onFocus: () => {},
  onBlur: () => {},
  value: '',
  defaultValue: ''
};

/**
 * Use the `Select` component to give the user multiple selections with and
 * enhanced design but, preserving a native select UI.
 *
 * Aside from the props listed below, this component accepts all the normal
 *  props from a select HTML tag; they will be streamed to the select tag.
 */
const Select = ({
  children,
  errorMessage,
  placeholder,
  onFocus,
  onBlur,
  value,
  id,
  defaultValue,
  invalid,
  borderColor,
  color,
  ...props
}) => {
  const [isFocusedOrFilled, setIsFocusedOrFilled] = useState(!!(value && value !== 'placeholder') || !!defaultValue);
  useEffect(() => {
    setIsFocusedOrFilled(!!(value && value !== 'placeholder') || !!defaultValue);
  }, [defaultValue, value]);

  const selectRef = useRef(null);

  const focusHandler = e => {
    if (!isFocusedOrFilled) {
      setIsFocusedOrFilled(true);
    }

    onFocus(e);
  };

  const blurHandler = e => {
    const {
      current: { value: selectValue }
    } = selectRef;
    setIsFocusedOrFilled(!!selectValue && selectValue !== 'placeholder');
    onBlur(e);
  };

  const getDefaultValue = () => {
    if (value && value !== 'placeholder') {
      return {};
    }

    if (defaultValue) {
      return { defaultValue };
    }

    if (placeholder) {
      return { defaultValue: 'placeholder' };
    }

    return {};
  };

  const getValue = () => {
    if (value && value !== 'placeholder') {
      return { value };
    }

    return {};
  };

  return (
    <Box>
      <Relative>
        <StyledSelect
          {...props}
          {...getValue()}
          {...getDefaultValue()}
          ref={selectRef}
          id={id}
          placeholder={placeholder}
          onFocus={focusHandler}
          onBlur={blurHandler}
          invalid={invalid}
          borderColor={borderColor}>
          {placeholder && (
            <option value="placeholder" hidden>
              {placeholder}
            </option>
          )}
          {children}
        </StyledSelect>
        {placeholder && (
          <StyledPlaceHolder color={color} htmlFor={id} active={isFocusedOrFilled} value={value}>
            {placeholder}
          </StyledPlaceHolder>
        )}
        <StyledChevronDownIcon size={14} />
      </Relative>
      {errorMessage && invalid && (
        <Flex minHeight="13px" alignItems="center">
          <Text
            as={'label'}
            fontWeight={fontWeights.medium}
            mt="8px"
            mb="8px"
            fontSize={9}
            color="digitalBrightRed.100">
            {errorMessage}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

Select.propTypes = propTypes;
Select.defaultProps = defaultProps;

export default Select;
